import { Lock, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import Input from "@/components/ui/Input";
import {
  getSession,
  isStaffRole,
  isStaffSession,
  setSession,
  type SessionRole,
} from "@/components/feature/auth/session";
import AuthShell from "./AuthShell";
import { authService } from "@/services/apiServices";

const ADMIN_PASSWORD = "admin123";
const RETAILER_PASSWORD = "demo123";

interface DemoAccount {
  label: string;
  id: string;
  roleType: "staff" | "retailer";
  badge: string;
}

const DEMO_ACCOUNTS: DemoAccount[] = [
  { label: "Super Admin", id: "admin@mandatepro.in", roleType: "staff", badge: "All Access" },
  { label: "Admin", id: "admin.ops@mandatepro.in", roleType: "staff", badge: "Operations" },
  { label: "Retailer Onboarding Executive", id: "onboarding@mandatepro.in", roleType: "staff", badge: "Onboarding" },
  { label: "Retailer Onboarding Executive & Wallet Request Handler", id: "onboarding.wallet@mandatepro.in", roleType: "staff", badge: "Onboarding & Wallet" },
  { label: "Wallet Handler", id: "wallet@mandatepro.in", roleType: "staff", badge: "Topups" },
  { label: "Retailer (Approved)", id: "9876543210", roleType: "retailer", badge: "Retailer Portal" },
];

export default function Login() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<{ identifier?: string; password?: string; api?: string }>({});
  const [loading, setLoading] = useState(false);

  // Auto-redirect if already logged in
  useEffect(() => {
    const s = getSession();
    if (s) {
      if (isStaffSession(s)) {
        navigate("/admin", { replace: true });
      } else if (s.role === "retailer") {
        navigate("/retailer", { replace: true });
      }
    }
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next: typeof errors = {};
    const cleanId = identifier.trim();
    const cleanPass = password.trim();

    if (!cleanId && !cleanPass) {
      next.identifier = "Please enter your mobile number or email.";
      next.password = "Please enter your password.";
      next.api = "Please enter both mobile number/email and password.";
    } else if (!cleanId) {
      next.identifier = "Please enter your mobile number or email.";
    } else if (!cleanPass) {
      next.password = "Please enter your password.";
    }

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    try {
      const response = await authService.login(cleanId, cleanPass);
      setLoading(false);

      if (response && response.success && response.data) {
        const { user, token } = response.data;
        const normalizedRole = (user.role || "").toLowerCase().replace(/[\s-]+/g, "_");
        const isStaff = Boolean(user.isStaff || isStaffRole(normalizedRole) || isStaffRole(user.role));

        if (isStaff) {
          // All 5 staff roles (super_admin, admin, retailer_onboarding_executive, retailer_onboarding_executive_walletrequesthandler, wallet_request_handler) redirect to /admin
          setSession({
            id: user.id,
            role: (normalizedRole || "admin") as SessionRole,
            isStaff: true,
            token,
            email: user.email,
            name: user.name,
            permissions: user.permissions || null,
            customPermissions: user.customPermissions || null,
          });
          navigate("/admin");
        } else {
          // Retailer role redirects to /retailer with unrestricted access to retailer pages
          setSession({
            id: user.id,
            role: "retailer",
            isStaff: false,
            retailerId: user.id,
            token,
            userCode: user.userCode || user.id,
            name: user.name,
            email: user.email,
            mobile: user.mobile,
            shopName: user.shopName,
            deviceId: user.deviceId,
            kycStatus: user.kycStatus,
            walletBalance: Number(user.walletBalance ?? 0),
            isEmailVerified: Boolean(user.isEmailVerified),
            merchantToken: user.merchant_token ?? null,
          });
          navigate("/retailer");
        }
      } else {
        setErrors({ api: response.message || "Invalid credentials" });
      }
    } catch (err: any) {
      setLoading(false);
      const msg = err?.message || "Invalid login credentials. Please try again.";
      const lowerMsg = msg.toLowerCase();

      const fieldErrors: typeof errors = { api: msg };
      if (
        lowerMsg.includes("user does not exist") ||
        lowerMsg.includes("not found") ||
        lowerMsg.includes("mobile number or email")
      ) {
        fieldErrors.identifier = msg;
      } else if (
        lowerMsg.includes("incorrect password") ||
        lowerMsg.includes("password")
      ) {
        fieldErrors.password = msg;
      }
      setErrors(fieldErrors);
    }
  }

  function fillAccount(account: DemoAccount) {
    setIdentifier(account.id);
    setPassword(account.roleType === "retailer" ? RETAILER_PASSWORD : ADMIN_PASSWORD);
    setErrors({});
  }

  return (
    <AuthShell>
      <h2 className="text-2xl font-semibold text-ink">Welcome back</h2>
      <p className="mt-1 text-sm text-ink-secondary">Sign in to your account</p>

      {errors.api && (
        <div className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700 border border-rose-200">
          {errors.api}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4" noValidate>
        <Input
          label="Mobile Number or Email"
          placeholder="Enter mobile or email"
          prefixIcon={<User className="h-[18px] w-[18px]" />}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          error={errors.identifier}
        />
        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          prefixIcon={<Lock className="h-[18px] w-[18px]" />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />

        <div className="flex items-center justify-between">
          <Checkbox
            label="Remember me"
            checked={remember}
            onChange={setRemember}
          />
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className="text-sm font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
          >
            Forgot password?
          </button>
        </div>

        <Button type="submit" fullWidth loading={loading}>
          Sign In
        </Button>

        <p className="text-center text-sm text-ink-secondary mt-1">
          Don&apos;t have an account?{" "}
          <button
            type="button"
            onClick={() => navigate("/register")}
            className="font-semibold text-primary-500 hover:text-primary-600 cursor-pointer"
          >
            Sign Up
          </button>
        </p>
      </form>

      {/* <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-600">
          Demo Accounts (Click to test roles)
        </p>
        <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {DEMO_ACCOUNTS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => fillAccount(a)}
              className="flex flex-col items-start rounded-md border border-primary-100 bg-white/80 p-2 text-left hover:border-primary-300 hover:bg-white transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between w-full">
                <span className="text-xs font-semibold text-slate-800">{a.label}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    a.roleType === "retailer"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-indigo-50 text-indigo-700 border border-indigo-200"
                  }`}
                >
                  {a.badge}
                </span>
              </div>
              <span className="mt-1 font-mono text-[11px] leading-snug text-slate-500">
                {a.id} / {a.roleType === "retailer" ? RETAILER_PASSWORD : ADMIN_PASSWORD}
              </span>
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Staff roles redirect to <strong>/admin</strong> with RBAC. Retailers redirect to <strong>/retailer</strong> with full retailer access.
        </p>
      </div> */}

      <p className="mt-10 text-center text-xs text-ink-muted">
        © 2026 MandatePro. All rights reserved.
      </p>
    </AuthShell>
  );
}